import { useSeoMeta } from '@unhead/react';
import { Suspense, useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { PsychedelicScene } from '@/components/scenes/PsychedelicScene';
import { PsychedelicControls } from '@/components/PsychedelicControls';
import { AudioProcessor } from '@/lib/audioProcessor';
import { ProjectNavigation } from '@/components/site/ProjectNavigation';
import type { PsyState } from '@/lib/psychedelic/constants';
import { CanvasErrorBoundary } from '@/components/CanvasErrorBoundary';

const DEFAULT_PSY: PsyState = {
  phenomenon: 'tunnel',
  paletteIndex: 0,
  dose: 0.65,
  reactivity: 1.0,
  postFx: true,
};

const AudioVisualizerPage = () => {
  useSeoMeta({
    title: "Audio Visualizer — Let's Talk",
    description: 'Real-time GPU psychedelic visualizer grounded in altered-state phenomenology.',
  });

  const processorRef = useRef<AudioProcessor | null>(null);
  if (processorRef.current === null) processorRef.current = new AudioProcessor();
  const processor = useMemo(() => processorRef.current!, []);

  const [psy, setPsyState] = useState<PsyState>(DEFAULT_PSY);
  const setPsy = (p: Partial<PsyState>) => setPsyState((s) => ({ ...s, ...p }));

  // Track audio active state for the overlay.
  const [audioActive, setAudioActive] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      setAudioActive(processor.active);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); };
  }, [processor]);

  useEffect(() => () => processor.stop(), [processor]);

  const handleMic = useCallback(async () => {
    try { await processor.startMic(); setShowOverlay(false); } catch {}
  }, [processor]);

  const handleUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (f) { try { await processor.startFile(f); setShowOverlay(false); } catch {} }
    };
    input.click();
  }, [processor]);

  const handleSkip = useCallback(() => setShowOverlay(false), []);

  return (
    <div>
      <div className="relative h-screen">
        <CanvasErrorBoundary fallback={<div className="w-full h-full bg-[#03020a]" />}>
          <Suspense fallback={null}>
            <PsychedelicScene processor={processor} psy={psy} />
          </Suspense>
        </CanvasErrorBoundary>

        {/* Fullscreen "tap to activate" overlay — shown until audio is started or dismissed */}
        {showOverlay && (
          <div className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-black/70 backdrop-blur-md">
            <div className="text-center space-y-6 px-6 max-w-md">
              <div className="space-y-2">
                <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  Audio-Reactive Visualizer
                </h1>
                <p className="text-sm text-white/50 leading-relaxed">
                  5 phenomena grounded in psychedelic neuroscience.
                  <br />
                  Feed it sound to bring it alive.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleMic}
                  className="w-full py-3.5 px-6 rounded-xl text-sm font-semibold tracking-wide uppercase
                    bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white
                    hover:from-purple-500 hover:to-fuchsia-500 active:scale-[0.98]
                    transition-all duration-200 shadow-lg shadow-purple-900/30"
                >
                  Use Microphone
                </button>
                <button
                  onClick={handleUpload}
                  className="w-full py-3.5 px-6 rounded-xl text-sm font-semibold tracking-wide uppercase
                    bg-white/10 border border-white/20 text-white
                    hover:bg-white/15 active:scale-[0.98]
                    transition-all duration-200"
                >
                  Upload Audio File
                </button>
                <button
                  onClick={handleSkip}
                  className="w-full py-2.5 text-xs text-white/30 hover:text-white/50 transition-colors"
                >
                  Skip — watch idle animation
                </button>
              </div>

              <p className="text-[10px] text-white/20 font-mono">
                Klüver form constants · V1 retinotopic mapping · Hyperbolic geometry · 5D-ASC dimensions
              </p>
            </div>
          </div>
        )}

        <PsychedelicControls processor={processor} psy={psy} setPsy={setPsy} />
      </div>
      <ProjectNavigation href="/portfolio/project-cyberscape" />
    </div>
  );
};

export default AudioVisualizerPage;
